import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { TwilioNumber } from '@marnic/shared';

type CapKey = 'voice' | 'sms' | 'fax';
const ALL_CAPS: CapKey[] = ['voice', 'sms', 'fax'];

type AppTwilioNumber = TwilioNumber & { provider?: string };

type Modal =
  | { type: 'add' }
  | { type: 'edit'; number: AppTwilioNumber }
  | { type: 'delete'; number: AppTwilioNumber }
  | null;

const EMPTY_ADD = { phone: '', label: '', provider: 'TWILIO', capabilities: ['voice'] as string[] };

function CapCheckboxes({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(cap: CapKey) {
    onChange(
      value.includes(cap) ? value.filter((c) => c !== cap) : [...value, cap],
    );
  }
  return (
    <div className="flex gap-4">
      {ALL_CAPS.map((cap) => (
        <label key={cap} className="flex items-center gap-1.5 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={value.includes(cap)}
            onChange={() => toggle(cap)}
            className="rounded border-ink-300"
          />
          {cap.charAt(0).toUpperCase() + cap.slice(1)}
        </label>
      ))}
    </div>
  );
}

export function NumberPoolSection() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<Modal>(null);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [editForm, setEditForm] = useState({ label: '', provider: 'TWILIO', capabilities: [] as string[] });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const numbersQuery = useQuery({
    queryKey: ['twilio-numbers'],
    queryFn: () => api.get<AppTwilioNumber[]>('/twilio-numbers'),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
  }

  const addNumber = useMutation({
    mutationFn: (data: typeof addForm) => api.post('/twilio-numbers', data),
    onSuccess: () => { invalidate(); setModal(null); setAddForm(EMPTY_ADD); setErrorMsg(null); },
    onError: (err: unknown) => setErrorMsg((err as { detail?: { error?: string } })?.detail?.error ?? 'Failed to add number'),
  });

  const editNumber = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof editForm }) =>
      api.patch(`/twilio-numbers/${id}`, data),
    onSuccess: () => { invalidate(); setModal(null); setErrorMsg(null); },
    onError: (err: unknown) => setErrorMsg((err as { detail?: { error?: string } })?.detail?.error ?? 'Failed to update number'),
  });

  const deleteNumber = useMutation({
    mutationFn: (id: string) => api.delete(`/twilio-numbers/${id}`),
    onSuccess: () => { invalidate(); setModal(null); setErrorMsg(null); },
    onError: (err: unknown) => setErrorMsg((err as { detail?: { error?: string } })?.detail?.error ?? 'Failed to delete number'),
  });

  function openEdit(n: AppTwilioNumber) {
    setEditForm({ label: n.label ?? '', provider: n.provider ?? 'TWILIO', capabilities: [...n.capabilities] });
    setErrorMsg(null);
    setModal({ type: 'edit', number: n });
  }

  function openDelete(n: AppTwilioNumber) {
    setErrorMsg(null);
    setModal({ type: 'delete', number: n });
  }

  function closeModal() {
    setModal(null);
    setErrorMsg(null);
  }

  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink-200">
        <h2 className="text-sm font-semibold text-ink-900">Number Pool</h2>
        <button
          className="btn-primary text-xs"
          onClick={() => { setAddForm(EMPTY_ADD); setErrorMsg(null); setModal({ type: 'add' }); }}
        >
          Add number
        </button>
      </header>

      {numbersQuery.isLoading && <p className="px-4 py-3 text-sm text-ink-400">Loading…</p>}

      {numbersQuery.data && (
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50">
            <tr>
              {['Phone', 'Provider', 'Area code', 'Label', 'Capabilities', 'Status', ''].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-ink-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {numbersQuery.data.map((n) => (
              <tr key={n.id} className="border-t border-ink-100 hover:bg-ink-50">
                <td className="px-4 py-2 font-mono text-xs">{n.phoneE164}</td>
                <td className="px-4 py-2">
                  <span className={`badge ${n.provider === 'VOIPLINE' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {n.provider ?? 'TWILIO'}
                  </span>
                </td>
                <td className="px-4 py-2 text-ink-600">{n.areaCode ?? '—'}</td>
                <td className="px-4 py-2 text-ink-600">{n.label ?? '—'}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {n.capabilities.map((c) => (
                      <span key={c} className="badge bg-brand-100 text-brand-700">{c}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span className={`badge ${n.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-700'}`}>
                    {n.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-2">
                    <button className="btn-ghost text-xs" onClick={() => openEdit(n)}>Edit</button>
                    <button className="btn-ghost text-xs text-red-600 hover:text-red-700" onClick={() => openDelete(n)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {numbersQuery.data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-ink-400">
                  No numbers in pool. Add a Twilio number to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}

      {/* Add modal */}
      {modal?.type === 'add' && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
          <div className="card w-full max-w-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-ink-900">Add number</h3>
            <div>
              <label className="label">Phone number (UK)</label>
              <input
                className="input"
                value={addForm.phone}
                onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+441234567890"
              />
            </div>
            <div>
              <label className="label">Provider</label>
              <select
                className="input"
                value={addForm.provider}
                onChange={(e) => setAddForm((f) => ({ ...f, provider: e.target.value }))}
              >
                <option value="TWILIO">Twilio</option>
                <option value="VOIPLINE">VP</option>
              </select>
            </div>
            <div>
              <label className="label">Label (optional)</label>
              <input
                className="input"
                value={addForm.label}
                onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="London sales line"
              />
            </div>
            <div>
              <label className="label">Capabilities</label>
              <CapCheckboxes
                value={addForm.capabilities}
                onChange={(v) => setAddForm((f) => ({ ...f, capabilities: v }))}
              />
            </div>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                className="btn-primary"
                disabled={addNumber.isPending}
                onClick={() => addNumber.mutate(addForm)}
              >
                {addNumber.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {modal?.type === 'edit' && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
          <div className="card w-full max-w-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-ink-900">Edit number</h3>
            <p className="text-xs text-ink-500 font-mono">{modal.number.phoneE164}</p>
            <div>
              <label className="label">Provider</label>
              <select
                className="input"
                value={editForm.provider}
                onChange={(e) => setEditForm((f) => ({ ...f, provider: e.target.value }))}
              >
                <option value="TWILIO">Twilio</option>
                <option value="VOIPLINE">VP</option>
              </select>
            </div>
            <div>
              <label className="label">Label</label>
              <input
                className="input"
                value={editForm.label}
                onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Capabilities</label>
              <CapCheckboxes
                value={editForm.capabilities}
                onChange={(v) => setEditForm((f) => ({ ...f, capabilities: v }))}
              />
            </div>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                className="btn-primary"
                disabled={editNumber.isPending}
                onClick={() => editNumber.mutate({ id: modal.number.id, data: editForm })}
              >
                {editNumber.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {modal?.type === 'delete' && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
          <div className="card w-full max-w-sm p-4 space-y-3">
            <h3 className="text-sm font-semibold text-ink-900">Delete number</h3>
            <p className="text-sm text-ink-700">
              Remove <span className="font-mono font-semibold">{modal.number.phoneE164}</span> from the pool?
              This cannot be undone.
            </p>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                className="btn-danger"
                disabled={deleteNumber.isPending}
                onClick={() => deleteNumber.mutate(modal.number.id)}
              >
                {deleteNumber.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
